import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, Star, MapPin, Baby, Blocks, GraduationCap, Bus, UtensilsCrossed, ShieldCheck } from "lucide-react";
import { FloatingRocket } from "@/components/Rocket";
import { Photo } from "@/components/Photo";
import { Marquee } from "@/components/Marquee";
import { Testimonials } from "@/components/Testimonials";
import { Features } from "@/components/Features";
import { AcceptanceStamps } from "@/components/AcceptanceStamps";
import { FadeUp, MaskedLines, ChapterNumber } from "@/components/Reveal";
import { PHOTOS, LOCATIONS, MANTRAS } from "@/data/site";
import { useLang, hlLines } from "@/i18n/LanguageContext";

const FADE_IN = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7 } };
const CLIP_REVEAL = {
  initial: { clipPath: "inset(100% 0 0 0)" },
  animate: { clipPath: "inset(0% 0 0 0)" },
  transition: { duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] },
};
const POP = { initial: { opacity: 0, scale: 0.6 }, animate: { opacity: 1, scale: 1 } };
const STAR_POP = { initial: { opacity: 0, scale: 0, rotate: -30 }, whileInView: { opacity: 1, scale: 1, rotate: 0 }, viewport: { once: true } };
const MANTRA_POS = ["-right-2 top-10", "-left-3 top-1/3", "-right-4 bottom-1/3", "left-6 -bottom-3"];
const MANTRA_TRANSITION = MANTRA_POS.map((_, i) => ({ delay: 0.9 + i * 0.12, type: "spring", stiffness: 200 }));
const STAR_TRANSITION = [0, 1, 2, 3].map((i) => ({ delay: i * 0.12, type: "spring", stiffness: 220 }));

const HeroPhoto = ({ photoY, rocketY }) => {
  const { t } = useLang();
  return (
    <div className="relative lg:col-span-5">
      <motion.div style={{ y: photoY }} className="relative">
        <motion.div {...CLIP_REVEAL} className="overflow-hidden rounded-t-[11rem] rounded-b-[2rem] border-4 border-white shadow-2xl">
          <Photo priority src={PHOTOS.fryClassroom} alt={t("home.heroAlt")} className="aspect-[4/5] w-full object-cover" sizes="(max-width: 1024px) 100vw, 40vw" data-testid="hero-photo" />
        </motion.div>
        <motion.div style={{ y: rocketY }} className="absolute -left-10 -top-8 w-24 sm:-left-14 sm:w-32">
          <FloatingRocket />
        </motion.div>
      </motion.div>

      {MANTRAS.slice(0, 4).map((word, i) => (
        <motion.span
          key={word}
          {...POP}
          transition={MANTRA_TRANSITION[i]}
          className={`absolute rounded-full bg-white px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-widest text-brand shadow-lg ${MANTRA_POS[i]}`}
          data-testid={`mantra-badge-${i}`}
        >
          {word}
        </motion.span>
      ))}
    </div>
  );
};

const HeroCopy = () => {
  const { t } = useLang();
  return (
          <div className="lg:col-span-7">
            <motion.p
              {...FADE_IN}
              className="font-mono text-xs font-medium uppercase tracking-[0.3em] text-brand"
              data-testid="hero-eyebrow"
            >
              {t("home.eyebrow")}
            </motion.p>

            <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-[4.6rem]" data-testid="hero-headline">
              <MaskedLines inView={false} delay={0.15} lines={hlLines(t("home.heroLines"))} />
            </h1>

            <FadeUp delay={0.55} className="mt-7 max-w-xl">
              <p className="text-base leading-relaxed text-mist sm:text-lg">{t("home.heroSub")}</p>
            </FadeUp>

            <FadeUp delay={0.7} className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/book"
                data-testid="hero-book-tour-btn"
                className="group flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-display text-base font-semibold text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-brandsky hover:shadow-xl"
              >
                {t("home.bookTour")}
                <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              {LOCATIONS.map((loc) => (
                <a
                  key={loc.id}
                  href={loc.phoneHref}
                  data-testid={`hero-call-btn-${loc.id}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-full border-2 border-ink/15 px-5 py-[0.9rem] font-display text-base font-semibold transition-colors duration-200 hover:border-brand hover:text-brand"
                >
                  <Phone className="h-4 w-4" />
                  <span className="font-mono text-[11px] uppercase tracking-wider text-mist">{loc.short}</span>
                  {loc.phone}
                </a>
              ))}
            </FadeUp>

            <FadeUp delay={0.85} className="mt-10">
              <div className="flex flex-wrap gap-x-8 gap-y-3 font-mono text-xs uppercase tracking-widest text-mist" data-testid="hero-stats">
                {t("home.stats").map((s) => <span key={s}>{s}</span>)}
                <span className="flex items-center gap-1.5 text-brand">
                  <Star className="h-3.5 w-3.5 fill-honey text-honey" />
                  {t("home.trs")}
                </span>
              </div>
            </FadeUp>
          </div>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const rocketY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} className="grain relative overflow-hidden pt-32 sm:pt-40" data-testid="home-hero">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-honey/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-40 top-64 h-[28rem] w-[28rem] rounded-full bg-brand/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-12 sm:mb-16"><AcceptanceStamps /></div>
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <HeroCopy />
          <HeroPhoto photoY={photoY} rocketY={rocketY} />
        </div>
      </div>
    </section>
  );
};

const LocationsPreview = () => {
  const { t } = useLang();
  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:py-32" data-testid="home-locations-section">
      <FadeUp>
        <ChapterNumber n={t("home.locChapter")} />
        <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
          <MaskedLines lines={hlLines(t("home.locLines"))} />
        </h2>
      </FadeUp>

      <div className="mt-14 grid gap-8 lg:grid-cols-2">
        {LOCATIONS.map((loc, i) => (
          <FadeUp key={loc.id} delay={i * 0.12}>
            <Link
              to="/locations"
              data-testid={`home-location-card-${loc.id}`}
              className="group block overflow-hidden rounded-[2rem] border border-ink/10 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
            >
              <div className="relative overflow-hidden">
                <Photo
                  src={loc.photo}
                  alt={t(`locations.${loc.id}.alt`)}
                  className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 rounded-full bg-cream/90 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-brand backdrop-blur">
                  {t(`locations.${loc.id}.badge`)}
                </span>
              </div>
              <div className="flex items-start justify-between gap-4 p-7">
                <div>
                  <h3 className="font-display text-xl font-semibold sm:text-2xl">{t(`locations.${loc.id}.name`)}</h3>
                  <p className="mt-2 flex items-center gap-1.5 text-sm text-mist">
                    <MapPin className="h-4 w-4" /> {loc.address}
                  </p>
                  <p className="mt-1 font-mono text-xs tracking-wider text-mist">{loc.phone}</p>
                </div>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-ink/15 transition-all duration-300 group-hover:border-brand group-hover:bg-brand group-hover:text-white">
                  <ArrowRight className="h-5 w-5" />
                </span>
              </div>
            </Link>
          </FadeUp>
        ))}
      </div>
    </section>
  );
};

const PROGRAM_ICONS = [Baby, Blocks, GraduationCap, Bus];
const PROGRAM_IDS = ["infants", "toddlers", "preschoolers", "school-agers"];

const ProgramsPreview = () => {
  const { t } = useLang();
  return (
    <section className="bg-sand py-24 lg:py-32" data-testid="home-programs-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <FadeUp>
          <ChapterNumber n={t("home.progChapter")} />
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="max-w-xl font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
              <MaskedLines lines={hlLines(t("home.progLines"))} />
            </h2>
            <Link
              to="/programs"
              data-testid="home-programs-more-link"
              className="group flex items-center gap-2 font-display text-base font-semibold text-brand"
            >
              {t("home.progMore")}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeUp>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {t("home.programs").map((p, i) => {
            const Icon = PROGRAM_ICONS[i];
            return (
              <FadeUp key={PROGRAM_IDS[i]} delay={i * 0.1}>
                <Link
                  to="/programs"
                  data-testid={`home-program-card-${PROGRAM_IDS[i]}`}
                  className="group flex h-full flex-col rounded-3xl border border-ink/10 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-brand/30 hover:shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <Icon className="h-7 w-7 text-brand" />
                    <span className="font-mono text-xs tracking-[0.3em] text-mist">0{i + 1}</span>
                  </div>
                  <h3 className="mt-6 font-display text-xl font-semibold">{p.title}</h3>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-honey">{p.age}</p>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-mist">{p.desc}</p>
                </Link>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const SPOTS = [
  { id: "kitchen", src: PHOTOS.fryKitchen, icon: UtensilsCrossed },
  { id: "fry-reception", src: PHOTOS.fryReception, icon: Star },
  { id: "clay-reception", src: PHOTOS.clayReception, icon: ShieldCheck },
];

const Spotlight = () => {
  const { t } = useLang();
  const captions = t("home.spots");
  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:py-32" data-testid="home-spotlight-section">
      <FadeUp>
        <ChapterNumber n={t("home.spotChapter")} />
        <h2 className="mt-4 max-w-2xl font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
          <MaskedLines lines={hlLines(t("home.spotLines"))} />
        </h2>
      </FadeUp>

      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {SPOTS.map((s, i) => (
          <FadeUp key={s.id} delay={i * 0.12} className={i === 0 ? "md:row-span-2" : ""}>
            <figure className="group relative h-full overflow-hidden rounded-[1.75rem] shadow-md">
              <Photo
                src={s.src}
                sizes="(max-width: 768px) 100vw, 33vw"
                alt={captions[i].alt}
                className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${i === 0 ? "aspect-[3/4] md:h-full" : "aspect-[4/3]"}`}
                data-testid={`spotlight-photo-${i}`}
              />
              <figcaption className="absolute inset-x-4 bottom-4 flex items-center gap-2 rounded-xl bg-ink/70 px-4 py-3 text-xs font-medium text-cream backdrop-blur-md">
                <s.icon className="h-4 w-4 shrink-0 text-honey" /> {captions[i].cap}
              </figcaption>
            </figure>
          </FadeUp>
        ))}
      </div>
    </section>
  );
};

const STAR_KEYS = ["one", "two", "three", "four"];

const StarBand = () => {
  const { t } = useLang();
  return (
    <section className="grain relative overflow-hidden bg-honey py-20 lg:py-24" data-testid="home-star-band">
      <div className="relative mx-auto flex max-w-7xl flex-col items-center gap-10 px-4 sm:px-6 lg:flex-row lg:justify-between">
        <FadeUp>
          <div className="flex gap-2" data-testid="star-band-stars">
            {STAR_KEYS.map((k, i) => (
              <motion.span
                key={k}
                {...STAR_POP}
                transition={STAR_TRANSITION[i]}
              >
                <Star className="h-9 w-9 fill-ink text-ink sm:h-11 sm:w-11" />
              </motion.span>
            ))}
          </div>
          <h2 className="mt-6 max-w-xl font-display text-2xl font-semibold leading-tight tracking-tight text-ink sm:text-3xl lg:text-4xl">
            {t("home.starTitle")}
          </h2>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-ink/70">{t("home.starBody")}</p>
        </FadeUp>
        <FadeUp delay={0.2}>
          <Link
            to="/book"
            data-testid="star-band-cta-btn"
            className="group flex items-center gap-2 rounded-full bg-ink px-8 py-4 font-display text-base font-semibold text-cream shadow-xl transition-all duration-200 hover:-translate-y-0.5"
          >
            {t("home.starCta")}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};

const FinalCta = () => {
  const { t } = useLang();
  return (
    <section className="grain relative overflow-hidden bg-ink py-24 lg:py-32" data-testid="home-final-cta">
      <div className="pointer-events-none absolute -bottom-24 -right-16 w-72 opacity-20 sm:w-96">
        <FloatingRocket />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <h2 className="max-w-3xl font-display text-3xl font-semibold leading-[1.1] tracking-tight text-cream sm:text-4xl lg:text-5xl" data-testid="final-cta-headline">
          <MaskedLines lines={hlLines(t("home.finalLines"))} />
        </h2>
        <FadeUp delay={0.3} className="mt-10 flex flex-wrap gap-4">
          <Link
            to="/book"
            data-testid="final-cta-book-btn"
            className="group flex items-center gap-2 rounded-full bg-honey px-8 py-4 font-display text-base font-semibold text-ink shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl"
          >
            {t("home.bookTour")}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/book?type=enroll"
            data-testid="final-cta-enroll-btn"
            className="rounded-full border-2 border-cream/25 px-8 py-[0.9rem] font-display text-base font-semibold text-cream transition-colors duration-200 hover:border-honey hover:text-honey"
          >
            {t("home.finalEnroll")}
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee className="mt-20" />
      <Features />
      <LocationsPreview />
      <ProgramsPreview />
      <Spotlight />
      <StarBand />
      <Testimonials />
      <FinalCta />
    </>
  );
}
